//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  TextField       ,
  Box             ,
  Button          ,
  IconButton      ,
  Table           ,
  TableHead       ,
  TableBody       ,
  TableRow        ,
  TableCell       ,
  TableFooter     ,
  TablePagination ,
  Typography      ,
  InputAdornment  ,
  Snackbar        ,
  Alert           ,
} from '@mui/material';

import {
  FirstPage          ,
  KeyboardArrowLeft  ,
  KeyboardArrowRight ,
  LastPage           ,
  UnfoldMoreOutlined ,
  Search             ,
  Save               ,
  Update             ,
} from '@mui/icons-material';

// === COMPONENTS ===
import { DeleteBatchDialog } from './DeleteBatchDialog/DeleteBatchDialog';
import { BulkUpdateDialog } from './BulkUpdateDialog/BulkUpdateDialog';

// === API ===
import {
  onMonitorBatches            ,
  removeListener              ,
  workflowMonitorBatchPriority,
  workflowMonitorBatchDelete  ,
  workflowMonitorSettings     ,
  workflowMonitorBatchDownload,
  on,
} from 'api/prokey/prokey.websocket';


//#endregion

//-----------------------------------------------------------------
function TablePaginationActions(props) {
  //-----------------------------------------------------------------
  const { count, page, rowsPerPage, onPageChange } = props;

  const last_page = Math.ceil( count / rowsPerPage ) -1;
  const is_page_first = page <= 0;
  const is_page_final = page >= last_page;

  //-----------------------------------------------------------------
  const _handleClick_PageFirst = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, 0);
  };

  //-----------------------------------------------------------------
  const _handleClick_PagePrev = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, page - 1);
  };

  //-----------------------------------------------------------------
  const _handleClick_PageNext = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, page + 1);
  };

  //-----------------------------------------------------------------
  const _handleClick_PageFinal = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, Math.max(0, last_page));
  };

  return (
    <Box sx={{ flexShrink: 0 }} >
      <IconButton onClick={ _handleClick_PageFirst } disabled={ is_page_first } aria-label='first page'>
        <FirstPage />
      </IconButton>
      <IconButton onClick={ _handleClick_PagePrev  } disabled={ is_page_first } aria-label='previous page'>
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton onClick={ _handleClick_PageNext  } disabled={ is_page_final } aria-label='next page'>
        <KeyboardArrowRight />
      </IconButton>
      <IconButton onClick={ _handleClick_PageFinal } disabled={ is_page_final } aria-label='last page'>
        <LastPage />
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

//-----------------------------------------------------------------
export function BatchPanel() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const default_page =  0;

  const [ batches     , setBatches     ] = useState( [] );
  const [ total       , setTotal       ] = useState( 0 );
  const [ page        , setPage        ] = useState( default_page );
  const [ rowsPerPage , setRowsPerPage ] = useState( 15 );
  const [ search      , setSearch      ] = useState( '' );

  const [ priority       , setPriority      ] = useState( 100 );
  const [ selected_item  , setSelectedItem  ] = useState( null );
  const [ selected_items , setSelectedItems ] = useState( {} );

  const [ is_open_delete , setIsOpenDelete ] = useState( false );
  const [ is_open_bulk_update , setIsOpenBulkUpdate   ] = useState( false );
  const [ snackbar_open, setSnackbarOpen ] = useState(false);
  const [ snackbar_message, setSnackbarMessage ] = useState(
    'Bulk update submitted. Processing may take some time depending on the number of batches.'
  );

  function onBulkUpdateSuccess() {
    //-----------------------------------------------------------------
    setSnackbarMessage('Bulk update submitted. Processing may take some time depending on the number of batches.');
    setSnackbarOpen(true);
  }

  //#endregion
  //#region [STYLES]

  const _styles = {
    container: {
      height: '100%',
      overflowX: 'auto',
      width: '100%',
    },
    title_container: {
      display: 'flex',
      padding: '0.25rem',
    },
    title: {
      display: 'inline-block',
      padding: '0 1rem',
    },
    priority_text: {
      marginTop : '0.5rem',
      marginBottom : '0.5rem',
      width  : '10rem',
    },
    priority_button: {
      marginLeft : '1rem',
      height     : '4rem',
    },
    delete_button: {
      marginLeft : '1rem',
      height     : '4rem',
    },
    search_container: {
      display    : 'flex' ,
      width      : '45%'  ,
      zindex     : 1      ,
      marginLeft : 'auto' ,
      alignItems : 'center',
    },
    search: {
      marginLeft : '1rem',
      width  : '100%',
      height : '4rem',
      zindex : 1000,
    },
    table: {
      tableLayout : 'auto',
      width       : '100%',
    },
    table_row: {
      cursor: 'pointer',
    },
    table_header_row: {
      cursor: 'pointer',
      overflowX: 'auto',
    },
    table_header_name: {
      minWidth: '9rem',
    },
    table_header_items: {
      minWidth: '9rem',
    },
    table_header_errors: {
      minWidth: '9rem',
    },
    table_header_percent: {
      minWidth: '9rem',
    },
    table_header_process: {
      minWidth: '9rem',
    },
    table_header_priority: {
      minWidth: '9rem',
    },
    table_header_timestamp: {
      minWidth: '9rem',
    },
    table_pagination: {
      width: '100%',
    },
    button_download: {
      marginLeft: '1rem' ,
      height: '3rem' ,
      width : '18rem',
    },
    button_bulk_update: {
      marginLeft : '1rem' ,
      height: '3rem' ,
      width : '18rem',
    },
    footer_actions_container: {
      display       : 'flex',
      alignItems    : 'center',
      gap           : '1rem',
      justifyContent: 'flex-start',
    },
  }

  const
    CONTAINER = {
      sx: _styles.container,
    },
    TITLE_CONTAINER = {
      sx: _styles.title_container,
    },
    TITLE = {
      sx       : _styles.title,
      variant  : 'h6',
      color    : 'primary',
      children : `Total Batches: ${total ? total : 0}`
    },
    PRIORITY_TEXT = {
      sx           : _styles.priority_text,
      variant      : 'outlined',
      label        : 'Priority',
      value        : priority  ,
      onKeyDown    : onKeyDown_Priority,
      onChange     : onChange_Priority,
      name         : 'batch_priority_input',
      InputLabelProps: {
        shrink: true,
      }
    },
    PRIORITY_BUTTON = {
      sx       : _styles.priority_button,
      color    : 'primary',
      variant  : 'contained',
      disabled : Object.keys(selected_items).length > 0 ? false : true,
      onClick  : onClick_PriorityButton,     
      children : 'Priority',
      name     : 'set_batch_priority_btn',
    },
    DELETE_BUTTON = {
      sx       : _styles.delete_button,
      color    : 'error',
      variant  : 'contained',
      disabled : Object.keys(selected_items).length > 0 ? false : true,
      onClick  : onClick_BatchDelete,
      children : 'Delete',
      name     : 'delete_batch_btn',
    },
    SEARCH_CONTAINER = {
      sx : _styles.search_container,
    },
    SEARCH = {
      sx           : _styles.search,
      id           : 'input-with-icon-textfield',
      variant      : "standard",
      label        : 'Filter Batches (Substring Search)',
      value        : search,
      autoComplete : 'off',
      onChange     : onChange_Search,
      onKeyDown    : onKeyDown_Search,
      size         : "small",
      name         : 'filter_batches_input',
      InputProps   : {
        startAdornment: (
          <InputAdornment position='start'>
            <Search />
          </InputAdornment>
        ),
      }
    },
    TABLE = {
      sx   : _styles.table,
      size : 'small',
      key  : 0,
      stickyHeader : true,
    },
    TABLE_HEADER_ROW = {
      sx : _styles.table_header_row,
    },
    TABLE_HEADER_NAME = {
      sx : _styles.table_header_name,
      onClick : () => onClick_Header( 'name' ),
      name : 'filter_batches_name_btn',
    },
    TABLE_HEADER_ITEMS = {
      sx : _styles.table_header_items,
      onClick : () => onClick_Header( 'number_work_items' ),
      name : 'filter_batches_number_work_items_btn',
    },
    TABLE_HEADER_ERRORS = {
      sx : _styles.table_header_errors,
      onClick : () => onClick_Header( 'errors' ),
      name : 'filter_batches_errors_btn',
    },
    TABLE_HEADER_PERCENT = {
      sx : _styles.table_header_percent,
      onClick : () => onClick_Header( 'percent_complete' ),
      name : 'filter_batches_percent_complete_btn',
    },
    TABLE_HEADER_PROCESS = {
      sx : _styles.table_header_process,
      onClick : () => onClick_Header( 'process_name' ),
      name : 'filter_batches_process_name_btn',
    },
    TABLE_HEADER_STAGE = {
      sx : _styles.table_header_stage,
      onClick : () => onClick_Header( 'current_stage' ),
    },
    TABLE_HEADER_PRIORITY = {
      sx : _styles.table_header_priority,
      onClick : () => onClick_Header( 'priority' ),
      name : 'filter_batches_priroity_btn',
    },
    TABLE_HEADER_TIMESTAMP = {
      sx : _styles.table_header_timestamp,
      onClick : () => onClick_Header( 'time_started' ),
      name : 'filter_batches_time_started_btn',
    },
    TABLE_PAGINATION = {
      sx                 : _styles.table_pagination,
      colSpan            : 7,
      count              : total ? total : 0,
      page               : page,
      rowsPerPage        : rowsPerPage,
      rowsPerPageOptions : [15, 25, 50, 100, 200, 500],
      name               : 'select_rows_per_page_filter',
      SelectProps : {
        inputProps: { 'aria-label': 'rows per page' },
        native: true,
      },
      onPageChange        : handleChangePage,
      onRowsPerPageChange : handleChangeRowsPerPage,
      ActionsComponent    : TablePaginationActions,
    },
    BUTTON_DOWNLOAD = {
      sx       : _styles.button_download,
      variant  : 'contained',
      startIcon: <Save />,
      onClick  : () => workflowMonitorBatchDownload(),
      children : ['Download (.csv)'],
      name     : 'download_batch_info-btn',
    },
    BUTTON_BULK_UPDATE = {
      sx       : _styles.button_bulk_update,
      variant  : 'contained',
      startIcon: <Update />,
      onClick  : () => onClick_BulkUpdate(),
      children : ['Bulk Update (.csv)'],
      name     : 'bulk_update-btn',
    };

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleBatches( data ) {
      //-----------------------------------------------------------------
      setBatches( data.items  );
      setTotal  ( data.count  );
      setPage   ( data.page   );
      setSearch ( data.search );
    }

    //-----------------------------------------------------------------
    function handleBatchDownload( data ){
      //-----------------------------------------------------------------
      const csv_string = createCsvString(data.batches);
      downloadCsv(csv_string);
    };

    onMonitorBatches( handleBatches );
    on( 'WORKFLOW-MONITOR-BATCHES-DOWNLOAD', handleBatchDownload );

    return () => {
      removeListener('WORKFLOW-MONITOR-BATCHES'         , handleBatches       );
      removeListener('WORKFLOW-MONITOR-BATCHES-DOWNLOAD', handleBatchDownload );
    };
  }, []);

  //#endregion

  //#region [EVENTS] 
  
  //-----------------------------------------------------------------
  function onClick_Header(name) {
    //-----------------------------------------------------------------
    workflowMonitorSettings( { sort_header: name } );
  };

  //-----------------------------------------------------------------
  function onClick_Row(event, batch) {
    //-----------------------------------------------------------------
    const item            = batch.name;
    const is_action_multi = event.ctrlKey || event.metaKey; // Ctrl on Windows/Linux, Command on macOS
    const is_action_group = event.shiftKey;
    const is_selected     = selected_items.hasOwnProperty( item );
    const is_multiple     = selected_items.length > 1;

    let temp = { ...selected_items };

    if( !is_action_multi ) {
      temp = {};
    }

    if(  is_selected ) {
      delete temp[item]
    }

    if( !is_selected ) {
      temp[item] = item;
    }

    // if( is_action_group && !selected_item ) {      
    //   temp[item] = item;
    // }

    // if( is_action_group &&  selected_item ) {

    // }

    setSelectedItem ( item );
    setSelectedItems( temp );
  };

  //-----------------------------------------------------------------
  function onClick_BatchDelete() {
    //-----------------------------------------------------------------
    setIsOpenDelete( true );
  }

  //-----------------------------------------------------------------
  function onClick_PriorityButton() {
    //-----------------------------------------------------------------
    const value = Number(priority);
    if (Number.isNaN(value)) return; // optional guard
    workflowMonitorBatchPriority(Object.keys(selected_items), value);
    setSnackbarMessage('Priority update submitted.');
    setSnackbarOpen(true);
  }

  //-----------------------------------------------------------------
  function onClick_BulkUpdate() {
    //-----------------------------------------------------------------
    setIsOpenBulkUpdate( true );
  }

  //-----------------------------------------------------------------
  function onClose_BulkUpdate() {
    //-----------------------------------------------------------------
    setIsOpenBulkUpdate( false );
  }

  //-----------------------------------------------------------------
  function onKeyDown_Priority(event) {
    //-----------------------------------------------------------------
    const is_numeric = ['0','1','2','3','4','5','6','7','8','9'].includes( event.key );
    const is_valid = is_numeric || event.key === 'Delete' || event.key === 'Backspace' || event.key === 'ArrowLeft' || event.key === 'ArrowRight';
    if( !is_valid ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  //-----------------------------------------------------------------
  function onChange_Priority(event) {
    //-----------------------------------------------------------------
    const value = event.target.value;
    setPriority( value );
  };

  //-----------------------------------------------------------------
  function onChange_Search(event) {
    //-----------------------------------------------------------------
    const value = event.target.value;
    setSearch( value );
  };

  //-----------------------------------------------------------------
  function onKeyDown_Search(event) {
    //-----------------------------------------------------------------
    const is_submit = event.key === 'Enter';
    if (is_submit) {
      const value = event.target.value;
      workflowMonitorSettings( { search: value } );
    }
  };

  //-----------------------------------------------------------------
  function handleChangePage(event, newPage) {
    //-----------------------------------------------------------------
    setSelectedItems( {} );
    setSelectedItem ( null );

    setPage(newPage);
    workflowMonitorSettings( { page: newPage } );
  };

  //-----------------------------------------------------------------
  function handleChangeRowsPerPage(event) {
    //-----------------------------------------------------------------
    const myRowsPerPage = parseInt(event.target.value, 10);
    workflowMonitorSettings( { items_per_page: myRowsPerPage } );
    setRowsPerPage(myRowsPerPage);
    setPage(0);
  };

  //-----------------------------------------------------------------
  function onConfirm_BatchDelete() {
    //-----------------------------------------------------------------
    workflowMonitorBatchDelete(Object.keys(selected_items));
    setIsOpenDelete(false);
    setSnackbarMessage('Delete request submitted.');
    setSnackbarOpen(true);
  }

  //-----------------------------------------------------------------
  function onAbort_BatchDelete() {
    //-----------------------------------------------------------------
    setIsOpenDelete(false);
  }

  //#endregion

  //#region [METHOD]
  //-----------------------------------------------------------------
  function createCsvString(data) {
    //-----------------------------------------------------------------
    try {
      if(!data.length || data.length <= 0 ) return;
       
      const headers = Object.keys(data[0]);

      let result = headers.join(',') + '\n';
      for (let row of data) {
        result += Object.values(row).join(',') + '\n';
      }

      return result;
    } catch (error) {
      console.log(error);
    }
  }

  //-----------------------------------------------------------------
  function downloadCsv(csv_string) {
    //-----------------------------------------------------------------
    const file_name     = `batch-info-${ dayjs().format('YYYYMMDD') }.csv`;
    const csv_blob      = new Blob( [csv_string], { type: 'text/csv' } );
    const csv_url       = window.URL.createObjectURL( csv_blob );
    const csv_hyperlink = document.createElement('a');

    csv_hyperlink.setAttribute('href'    , csv_url  );
    csv_hyperlink.setAttribute('download', file_name);
    csv_hyperlink.click();
  }

  //-----------------------------------------------------------------
  function toPrettyName(input) {
    //-----------------------------------------------------------------
    if (!input) return '';

    return input
      .split('_')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box { ...CONTAINER }>
        <Box { ...TITLE_CONTAINER } >
          <DeleteBatchDialog is_open={is_open_delete} onConfirm={onConfirm_BatchDelete} onClose={onAbort_BatchDelete} />
          <BulkUpdateDialog is_open={is_open_bulk_update} onClose={onClose_BulkUpdate} onSuccess={onBulkUpdateSuccess} />
          <Typography { ...TITLE            } />
          <Box        { ...SEARCH_CONTAINER }  >
            <TextField { ...PRIORITY_TEXT   } />
            <Button    { ...PRIORITY_BUTTON } />
            <Button    { ...DELETE_BUTTON   } />
            <TextField { ...SEARCH          } />
          </Box>
        </Box>
        <Table {...TABLE } >
          <TableHead>
            <TableRow { ...TABLE_HEADER_ROW } >
              <TableCell { ...TABLE_HEADER_NAME } >
                <UnfoldMoreOutlined /> Batch
              </TableCell>
              <TableCell { ...TABLE_HEADER_ITEMS } >
                <UnfoldMoreOutlined /> Work Items
              </TableCell>
              <TableCell { ...TABLE_HEADER_ERRORS } >
                <UnfoldMoreOutlined /> Errors
              </TableCell>
              <TableCell { ...TABLE_HEADER_PERCENT } >
                <UnfoldMoreOutlined /> % Completed
              </TableCell>
              <TableCell { ...TABLE_HEADER_PROCESS } >
                <UnfoldMoreOutlined /> Process
              </TableCell>
              <TableCell {...TABLE_HEADER_STAGE} >
                <UnfoldMoreOutlined /> Stage
              </TableCell>
              <TableCell { ...TABLE_HEADER_PRIORITY } >
                <UnfoldMoreOutlined /> Priority
              </TableCell>
              <TableCell { ...TABLE_HEADER_TIMESTAMP } >
                <UnfoldMoreOutlined /> Start Time
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{displayBatchData()}</TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={8}>
                <Box sx={_styles.footer_actions_container}>
                  <Button { ...BUTTON_DOWNLOAD } />
                  <Button { ...BUTTON_BULK_UPDATE } />
                  <Box sx={{ marginLeft: 'auto' }}>
                    <TablePagination { ...TABLE_PAGINATION } />
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
        <Snackbar
          open={snackbar_open}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar_message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };

  //-----------------------------------------------------------------
  const displayBatchData = () => {
    //-----------------------------------------------------------------
    let result = [];
    batches.forEach((batch) => {

      const time_started = dayjs( batch.time_started * 1000 ).format('YYYY-MM-DD HH:mm A');
      const is_selected = selected_items.hasOwnProperty( batch.name );

      const style = {..._styles.table_row};
      if( is_selected ) style.backgroundColor = theme.palette.primary.main;

      result.push(
        <TableRow key={batch.name} id={batch.name} sx={style} onClick={ (event) => onClick_Row(event, batch)}>
          <TableCell key={0} >{ batch.name                        }</TableCell>
          <TableCell key={1} >{ batch.number_work_items           }</TableCell>
          <TableCell key={2} >{ batch.errors                      }</TableCell>
          <TableCell key={3} >{ batch.percent_complete            }</TableCell>
          <TableCell key={4} >{ batch.process_name                }</TableCell>
          <TableCell key={5} >{ toPrettyName(batch.current_stage) }</TableCell>
          <TableCell key={6} >{ batch.priority                    }</TableCell>
          <TableCell key={7} >{ time_started                      }</TableCell>
        </TableRow>
      );
    });

    const empty_rows = rowsPerPage - batches.length;
    if (empty_rows > 0) {
      result.push(
        <TableRow key={`empty_row`} style={{ height: 28 * empty_rows }}>
          <TableCell colSpan={8} key={`empty_cell`}></TableCell>
        </TableRow>
      );
    }

    return result;
  };

  //#endregion

  return display();
}
